import {
  Card,
  DangerTag,
  DefaultTag,
  Divider,
  Icon,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useCanViewClientPortalPricing } from "../../../components/permissions/Permissions";
import { INVOICE_STATUS } from "../../../constants";
import { InvoicesActionsDropdownMenu } from "../InvoicesActionsDropdownMenu";

interface Props {
  invoiceNumber: number;
  profileName: string;
  total: string | null;
  issueDate: string;
  status: string;
  id: string;
}
export function TableDataCard(props: Props) {
  const { invoiceNumber, profileName, total, issueDate, status, id } = props;
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();

  const getInvoiceLabel = (status: string) => {
    switch (status) {
      case INVOICE_STATUS.draft:
        return <DefaultTag key={INVOICE_STATUS.draft}>{t("Draft")}</DefaultTag>;
      case INVOICE_STATUS.open:
        return <DefaultTag key={INVOICE_STATUS.open}>{t("Open")}</DefaultTag>;
      case INVOICE_STATUS.paid:
        return <SuccessTag key={INVOICE_STATUS.paid}>{t("Paid")}</SuccessTag>;
      case INVOICE_STATUS.voided:
        return <DangerTag key={INVOICE_STATUS.voided}>{t("Voided")}</DangerTag>;
      case INVOICE_STATUS.failed_generation:
        return (
          <DangerTag key={INVOICE_STATUS.failed_generation}>
            {t("Failed Generation")}
          </DangerTag>
        );
      default:
        return "-";
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.headerContainer}>
        <Link
          to={id}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.invoiceLink}
          aria-label={t("Open Invoice {{number}}", { number: invoiceNumber })}
        >
          <div className={styles.invoiceNumberCell}>
            <div className={styles.invoiceIcon}>
              <Icon name="description" size="space-6" />
            </div>
            <div className={styles.invoiceNumberCellColumns}>
              <Text style="global/heading-medium">{invoiceNumber}</Text>
            </div>
          </div>
        </Link>
        <InvoicesActionsDropdownMenu invoiceId={id} />
      </div>
      <div
        className={styles.invoiceDetailsContainer}
        data-testid="invoiceDetailsContainer"
      >
        <div className={styles.profileDetails}>
          <div className={styles.topHalf}>
            <div className={styles.leftIndividual}>
              <Text style="global/heading-medium">{t("Billing profile")}</Text>
              <Text color="text-weak">{profileName}</Text>
            </div>

            <div className={styles.rightIndividual}>
              <Text style="global/heading-medium">{t("Issue date")}</Text>
              <Text color="text-weak">{issueDate}</Text>
            </div>
          </div>
          <Divider hideBorder marginTop="space-4" />
          <div className={styles.bottomHalf}>
            {canViewClientPortalPricing && (
              <div className={styles.leftIndividual}>
                <Text style="global/heading-medium">{t("Total")}</Text>
                <Text color="text-weak">{total}</Text>
              </div>
            )}

            <div className={styles.rightIndividual}>
              <Text style="global/heading-medium">{t("Status")}</Text>
              <Text color="text-weak">{getInvoiceLabel(status)}</Text>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

const styles = {
  invoiceLink: css({
    textDecoration: "none",
    color: "inherit",
  }),
  card: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    borderRadius: "var(--border-radius-3)",
  }),
  headerContainer: css({
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
  }),
  container: css({
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  }),
  invoiceDetailsContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    justifyContent: "space-between",
  }),
  profileDetailsContainer: css({
    display: "flex",
    alignItems: "center",
    gap: 10,
  }),
  profileDetails: css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }),
  tags: css({
    display: "flex",
    gap: "var(--space-2)",
    flexWrap: "wrap",
  }),
  invoiceNumberCell: css({
    ":hover": {
      cursor: "pointer",
    },
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "100%",
    marginBottom: "var(--space-4)",
  }),
  invoiceNumberCellColumns: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  }),
  invoiceIcon: css({
    marginRight: "10px",
    flexDirection: "column",
  }),
  topHalf: css({
    display: "flex",
    flexDirection: "row",
  }),
  bottomHalf: css({
    display: "flex",
    flexDirection: "row",
  }),
  leftIndividual: css({
    display: "flex",
    flexDirection: "column",
    marginRight: "var(--space-10)",
    width: "50%",
  }),
  rightIndividual: css({
    display: "flex",
    flexDirection: "column",
  }),
};
